import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaUsers,
  FaUserShield,
  FaTruckLoading,
  FaMapMarkerAlt,
  FaClipboardList,
  FaFlask,
  FaEdit,
  FaPlusSquare,
  FaVial,
  FaFileAlt,
  FaBoxOpen,
  FaBoxes,
  FaTruck,
  FaCertificate,
  FaTags,
  FaBalanceScale,
  FaExclamationTriangle,
  FaTruckMoving,
  FaSlidersH,
  FaDoorOpen,
  FaExchangeAlt,
  FaThLarge,
} from "react-icons/fa";
import { selectUser } from "../../features/userSlice";
import "./Dashboard.css";

const iconForMenu = (name = "") => {
  const n = String(name).toLowerCase();
  if (n.includes("user") && n.includes("role")) return FaUserShield;
  if (n.includes("assign role") || n.includes("roles")) return FaUserShield;
  if (n.includes("user")) return FaUsers;
  if (n.includes("assign station") || n.includes("station")) return FaMapMarkerAlt;
  if (n.includes("qc")) return FaFlask;
  if (n.includes("correct")) return FaEdit;
  if (n.includes("new unloading") && n.includes("qc")) return FaVial;
  if (n.includes("new unloading")) return FaPlusSquare;
  if (n.includes("my unloaded") || n.includes("unloaded")) return FaClipboardList;
  if (n.includes("slip")) return FaFileAlt;
  if (n.includes("unloading") || n.includes("unload")) return FaTruckLoading;
  if (n.includes("captive silco") || n.includes("silco captive")) return FaBoxes;
  if (n.includes("silco") || n.includes("bag")) return FaBoxOpen;
  if (n.includes("test certificate status") || n.includes("certificate status")) return FaCertificate;
  if (n.includes("test certificate") || n.includes("certificate")) return FaCertificate;
  if (n.includes("loading")) return FaTruck;
  if (n.includes("uhf") || n.includes("tag")) return FaTags;
  if (n.includes("re-weight") || n.includes("reweigh") || n.includes("re-weigh")) return FaBalanceScale;
  if (n.includes("stalled") || n.includes("stale")) return FaExclamationTriangle;
  if (n.includes("wb controller") || n.includes("controller")) return FaSlidersH;
  if (n.includes("empty gate") || n.includes("gate out")) return FaDoorOpen;
  if (n.includes("adjust to challan") || n.includes("adjust to dispatch") || n.includes("adjust challan") || n.includes("adjust dispatch")) return FaExchangeAlt;
  if (n.includes("captive")) return FaTruckMoving;
  return FaThLarge;
};

const Dashboard = () => {
  const [menuOptions, setMenuOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();

  const firstName = useMemo(() => {
    const full = String(user?.emp_name || user?.name || "").trim();
    if (!full) return "";
    return full.split(/\s+/)[0];
  }, [user]);

  useEffect(() => {
    if (token && emp_id) {
      const fetchMenuOptions = async () => {
        setLoading(true);
        try {
          const menuResponse = await axios.post("/api/menu/getMobMenuUserData", { emp_id });
          // Hide only New Unloading Entry duplicate; keep New Unloading QC Screen visible
          const rows = Array.isArray(menuResponse.data)
            ? menuResponse.data.filter(
              item => item.menu_url !== "/NewUnloadingEntry"
            )
            : [];
          const parentMenus = rows.filter((menu) => menu.parent_menu_id === 0);
          const childMenus = rows.filter((menu) => menu.parent_menu_id !== 0);

          const groupedMenuOptions = parentMenus.map((parent) => ({
            ...parent,
            children: childMenus.filter((child) => child.parent_menu_id === parent.menu_id),
          }));

          setMenuOptions(groupedMenuOptions);
        } catch (error) {
          console.error("Error fetching menu options:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchMenuOptions();
    } else {
      navigate("/login");
    }
  }, [token, emp_id, navigate]);

  const handleTileClick = (menuUrl) => {
    if (menuUrl) navigate(menuUrl);
  };

  const renderTile = (menu, key) => {
    const Icon = iconForMenu(menu.menu_name);
    return (
      <button
        key={key}
        type="button"
        className="dash-tile"
        onClick={() => handleTileClick(menu.menu_url)}
      >
        <span className="dash-tile-icon" aria-hidden="true">
          <Icon />
        </span>
        <span className="dash-tile-label">{menu.menu_name}</span>
      </button>
    );
  };

  return (
    <div className="dash-page">
      <header className="dash-hero">
        <p className="dash-kicker">Singhal Logistics</p>
        <h1 className="dash-title">
          {firstName ? `Hi ${firstName}` : "Operations Menu"}
        </h1>
        <p className="dash-sub">Choose a task to continue yard & weighbridge work.</p>
      </header>

      <div className="dash-body">
        {loading && <div className="dash-status">Loading menu…</div>}

        {!loading && menuOptions.length === 0 && (
          <div className="dash-status">No menu options assigned.</div>
        )}

        {menuOptions.map((parentMenu, index) =>
          parentMenu.menu_url ? (
            <section key={parentMenu.menu_id || index} className="dash-section">
              <div className="dash-tile-list">{renderTile(parentMenu, parentMenu.menu_id || index)}</div>
            </section>
          ) : (
            <section key={parentMenu.menu_id || index} className="dash-section">
              <h2 className="dash-section-title">{parentMenu.menu_name}</h2>
              <div className="dash-tile-list">
                {parentMenu.children.map((childMenu, childIndex) =>
                  renderTile(childMenu, childMenu.menu_id || `${index}-${childIndex}`)
                )}
              </div>
            </section>
          )
        )}

        {/* <section className="dash-section">
          <h2 className="dash-section-title">Material Change</h2>
          <div className="dash-tile-list">
            {renderTile({ menu_url: "/materialChange", menu_name: "Material Change" }, `123-45`)}
          </div>
        </section> */}
      </div>
    </div>
  );
};

export default Dashboard;
